import { PermissionsSlugs } from "@/enums/permissions.enum";
import { RolesSlugs } from "@/enums/roles.enum";
import Vue from "vue";
import Vuex, { StoreOptions } from "vuex";
import { RootState } from "./types";
import VuexPersistence from "vuex-persist";
import { DateTime } from "luxon";
import { User } from "@/dtos/user";
import { Permission } from "@/dtos/role";
import authService from "@/services/auth.service";
import aiAssistant from "./modules/aiAssistant";

const vuexLocal = new VuexPersistence<RootState>({
  storage: window.localStorage,
  // Persist only auth + sidebar prefs. The aiAssistant module is intentionally
  // excluded so a stale `isOpen: true` (or future per-session chat state) does
  // not survive logout/refresh.
  reducer: (state: any) => ({
    user: state.user,
    pinnedLinks: state.pinnedLinks,
  }),
});

Vue.use(Vuex);

const store: StoreOptions<RootState> = {
  state: {
    user: null,
    pinnedLinks: null,
  },
  actions: {
    async setUserAndPermissions(_, user: User) {
      const [permissions, profile] = await Promise.all([
        authService.FetchUserPermissions(user),
        authService.FetchUserProfile(user),
      ]);

      this.commit("setUserState", {
        ...user,
        ...profile,
        permissions,
      });
    },
    setUser(state, user) {
      this.commit("setUserState", user);
    },
    removeUser(state) {
      this.commit("setUserState", null);
    },
    setSidebarPinnedLinks(state, links) {
      this.commit("setSidebarPinnedLinksState", links);
    },
    reloadUser() {
      this.commit("reloadUser");
    },
  },
  getters: {
    profile(state) {
      return state.user;
    },
    isAuthenticated(state) {
      return state.user != null && state.user.accessToken != null;
    },
    havePermission: (state) => (permission: PermissionsSlugs) => {
      if (state.user?.role?.slug === RolesSlugs.Superadmin) return true;
      return (
        state.user != null &&
        state.user.permissions != null &&
        state.user.permissions.find((p) => p.slug == permission) != null
      );
    },
    havePermissions: (state) => (permissions: PermissionsSlugs[]) => {
      if (state.user?.role?.slug === RolesSlugs.Superadmin) return true;
      if (state.user == null || state.user.permissions == null) return false;
      return permissions.every((permission) =>
        state.user!.permissions!.find(
          (p: Permission) => p.slug == permission
        )
      );
    },
    sidebarPinnedLinks(state) {
      return state.pinnedLinks;
    },
  },
  modules: { aiAssistant },
  mutations: {
    setUserState(state, user) {
      state.user = user;
    },
    setSidebarPinnedLinksState(state, links) {
      state.pinnedLinks = links;
    },
    reloadUser(state) {
      if (!state || !state.user) return;
      const key = state.user.key;
      state.user.key = key + 1;
    },
  },
  plugins: [vuexLocal.plugin],
};

export default new Vuex.Store<RootState>(store);
